import { useEffect, useRef, useState, type ComponentType } from 'react'
import { Link } from '@tanstack/react-router'
import {
  ChevronDown,
  Sparkles,
  HeartPulse,
  PauseCircle,
  AlertTriangle,
  ShieldHalf,
  LifeBuoy,
  Library,
  ArrowRight,
  Menu,
  X,
} from 'lucide-react'
import { useVisibleTools } from '@/lib/tools'
import { COMMUNITY_PARTNER } from '@/lib/community-partners'
import { GranGuardMark } from './brand'

type IconCmp = ComponentType<{ className?: string }>

/**
 * Same navigation as the DCUC/NASAA headers, on white rather than navy.
 * This face is read by older adults, often on a phone or an older laptop,
 * so it takes the high-contrast treatment: dark text on white, 16px nav
 * (not 15), and generous hit targets.
 */
const BASICS: { slug: string; label: string; icon: IconCmp }[] = [
  { slug: 'new-age-of-scams', label: 'The New Age of Scams', icon: Sparkles },
  { slug: 'emotional-hijack', label: 'The Emotional Hijack', icon: HeartPulse },
  { slug: 'pause-and-verify', label: 'Pause and Verify', icon: PauseCircle },
  { slug: 'warning-signs', label: 'Spot the Warning Signs', icon: AlertTriangle },
  { slug: 'digital-self-defense', label: 'Digital Self-Defense', icon: ShieldHalf },
  { slug: 'if-youre-scammed', label: 'If You’re Scammed', icon: LifeBuoy },
]

/** Always-available resources; live tools are appended from the registry.
 *  Articles are reachable from the homepage band, not the menu (Lee, 2026-09-09). */
const RESOURCES: { to: string; label: string; icon: IconCmp }[] = [
  { to: '/scam-library', label: 'Scam Library', icon: Library },
]

export default function GranGuardHeader() {
  const [openMenu, setOpenMenu] = useState<'basics' | 'tools' | null>(null)
  const [mobileOpen, setMobileOpen] = useState(false)
  const ref = useRef<HTMLElement>(null)
  const tools = useVisibleTools()
  const resourceItems = [
    ...RESOURCES,
    ...tools.map(t => ({ to: t.to, label: t.label, icon: t.icon })),
  ]

  useEffect(() => {
    if (!openMenu) return
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpenMenu(null)
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpenMenu(null)
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [openMenu])

  return (
    <header ref={ref} className="sticky top-0 z-40 border-b border-white/10 bg-[#0a0620]">
      {COMMUNITY_PARTNER && (
        <div
          className="border-b border-white/10 sm:hidden"
          style={{ backgroundColor: COMMUNITY_PARTNER.colors.primary }}
        >
          <p className="mx-auto max-w-7xl px-6 py-2 text-sm leading-snug text-white/90">
            {COMMUNITY_PARTNER.partnerLabel ?? 'In partnership with'}{' '}
            <span className="font-semibold text-white">{COMMUNITY_PARTNER.name}</span>
          </p>
        </div>
      )}

      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-4">
        <div className="flex min-w-0 items-center gap-4">
          <Link to="/" className="shrink-0" aria-label="GranGuard home">
            <GranGuardMark onDark className="h-10" />
          </Link>
          {/* Partner credit: GranGuard mark, a divider, then the two-line
              "In partnership with" lockup. No partner seal here (Lee,
              2026-09-10): the seal lives in the hero and footer. Below sm the
              row can't fit logo + credit + hamburger, so the credit drops to
              a slim strip above the nav instead. */}
          {COMMUNITY_PARTNER && (
            <div className="hidden min-w-0 border-s border-white/15 ps-4 leading-tight sm:block">
              <p className="text-sm font-medium text-violet-300">
                {COMMUNITY_PARTNER.partnerLabel ?? 'In partnership with'}
              </p>
              <p className="line-clamp-2 text-base font-semibold text-white sm:text-lg">
                {COMMUNITY_PARTNER.name}
              </p>
            </div>
          )}
        </div>

        {/* One breakpoint decides: below md the menu collapses into the hamburger;
            from md (tablet portrait and up) the three items fit inline beside
            the logo, so nothing wraps and no hamburger shows. */}
        <nav className="hidden items-center gap-2 md:ms-auto md:flex md:justify-end lg:gap-5">
          <NavMenu
            label="The Course"
            isOpen={openMenu === 'basics'}
            onToggle={() => setOpenMenu(openMenu === 'basics' ? null : 'basics')}
            onItemClick={() => setOpenMenu(null)}
            items={BASICS.map(b => ({ to: `/basics/${b.slug}`, label: b.label, icon: b.icon }))}
            widthClass="w-[17rem]"
          />
          <NavMenu
            label="Tools"
            isOpen={openMenu === 'tools'}
            onToggle={() => setOpenMenu(openMenu === 'tools' ? null : 'tools')}
            onItemClick={() => setOpenMenu(null)}
            items={resourceItems}
          />
          <Link
            to="/about"
            className="cursor-pointer rounded-md px-3 py-2 text-base font-bold tracking-tight text-white/85 transition hover:text-white"
          >
            About
          </Link>
        </nav>

        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => setMobileOpen(v => !v)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-lg text-white transition hover:bg-white/10 md:hidden"
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-white/10 bg-[#0a0620] md:hidden">
          <nav className="mx-auto flex max-w-7xl flex-col gap-1 px-6 py-4">
            <p className="px-1 pt-2 text-sm font-semibold text-violet-300">
              The Course
            </p>
            {BASICS.map(b => (
              <Link
                key={b.slug}
                to="/basics/$basicsSlug"
                params={{ basicsSlug: b.slug }}
                onClick={() => setMobileOpen(false)}
                className="rounded-lg px-3 py-3 text-base font-medium text-white/85 transition hover:bg-white/5"
              >
                {b.label}
              </Link>
            ))}
            <div className="my-2 border-t border-white/10" />
            <Link to="/scam-library" onClick={() => setMobileOpen(false)} className="rounded-lg px-3 py-3 text-base font-medium text-white/85 transition hover:bg-white/5">
              Scam Library
            </Link>
            <Link to="/about" onClick={() => setMobileOpen(false)} className="rounded-lg px-3 py-3 text-base font-medium text-white/85 transition hover:bg-white/5">
              About
            </Link>
            <Link
              to="/basics/$basicsSlug"
              params={{ basicsSlug: 'new-age-of-scams' }}
              onClick={() => setMobileOpen(false)}
              className="mt-3 inline-flex items-center justify-center gap-2 rounded-lg bg-violet-600 px-4 py-3.5 text-base font-semibold text-white"
            >
              Start the course
              <ArrowRight className="h-4 w-4" />
            </Link>
          </nav>
        </div>
      )}
    </header>
  )
}

function NavMenu({
  label,
  isOpen,
  onToggle,
  onItemClick,
  items,
  widthClass = 'w-[22rem]',
}: {
  label: string
  isOpen: boolean
  onToggle: () => void
  onItemClick: () => void
  items: { to: string; label: string; icon: IconCmp }[]
  widthClass?: string
}) {
  return (
    <div className="relative">
      <button
        onClick={onToggle}
        className="flex cursor-pointer items-center gap-1.5 rounded-md px-3 py-2 text-base font-bold tracking-tight text-white/85 transition hover:text-white"
      >
        {label}
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className={`absolute left-1/2 z-50 mt-3 -translate-x-1/2 rounded-2xl bg-white p-2 text-slate-700 shadow-[0_20px_50px_-12px_rgba(15,23,42,0.25)] ring-1 ring-slate-200/70 ${widthClass}`}>
          <ul>
            {items.map(item => {
              const Icon = item.icon
              return (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    onClick={onItemClick}
                    className="group flex items-center gap-3.5 rounded-xl px-3 py-2 text-base font-medium text-slate-700 transition hover:bg-violet-50 hover:text-slate-900"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center text-violet-600">
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="flex-1 leading-tight">{item.label}</span>
                  </Link>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}
